<template>
  <panel>
    <div class="header">
      <div>
        <icon class="icon" :glyph="icon" size="24" />
        <h3>{{ title }}</h3>
      </div>
      <el-select
        style="width: 275px"
        v-if="variantTestingChannel && selectedMethod"
        :value="selectedMethod"
        @change="val => $emit('on-method-change', val)"
      >
        <el-option
          v-for="item in methods[variantTestingChannel]"
          :key="item"
          :label="$t(`CampaignChannelAnalytics_ChannelMetrics_${item}`)"
          :value="item"
        >
        </el-option>
      </el-select>
    </div>
    <div class="chart-ct">
      <component
        :is="components[chartType]"
        :chart-data="chartData"
        :options="combinedChartOptions"
        :style="`height:${height}px; width: 100%`"
        :ref="`chart-${legendId}`"
      />
      <div class="legend-ct" :id="legendId"></div>
    </div>
  </panel>
</template>

<script>
import Panel from '@/common/components/Panel'
import md5 from 'md5'
import BarChartWrapper from '@/modules/customer-intelligence/components/BarChartWrapper.vue'
import LineChartWrapper from '@/modules/customer-intelligence/components/LineChartWrapper.vue'
import { statMethods } from '@/modules/campaign-manage/enum/VariantTestingMetrics'
import _ from 'lodash'

export default {
  name: 'ChannelMetricsChart',
  components: { LineChartWrapper, Panel },

  props: {
    selectedMethod: {
      type: String,
      required: false,
      default: null
    },
    variantTestingChannel: {
      type: String,
      required: false,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    chartType: {
      type: String,
      required: false,
      default: 'line'
    },
    icon: {
      type: String,
      required: true
    },
    chartData: {
      type: Object,
      required: true
    },
    chartOptions: {
      type: Object,
      required: false,
      default: () => {}
    },
    height: {
      type: Number,
      required: false,
      default: 250
    },
    alignTicksWithSecondAxis: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      statMethods,
      components: {
        bar: BarChartWrapper,
        line: LineChartWrapper
      },
      legendId: md5(
        'chart-legend-' +
          Math.random()
            .toString(36)
            .slice(2, 7)
      ),
      defaultChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          yAxes: [
            {
              id: 'y0',
              type: 'linear',
              beginAtZero: true,
              ticks: {
                min: 0,
                max: this.getMaxValue(),
                stepSize: this.calculateStepSizeY0(),
                precision: 0
              }
            },
            {
              id: 'y1',
              type: 'linear',
              display: 'auto',
              position: 'right',
              ticks: {
                min: 0,
                max: 100,
                stepSize: this.calculateStepSizeY1(),
                precision: 0,
                callback: function(value) {
                  return ((value / this.max) * 100).toFixed(0) + '%'
                }
              },
              gridLines: {
                drawOnChartArea: false
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ]
        },
        pointStyle: 'rect',
        legend: {
          display: false
        },
        legendCallback: function(chart) {
          const text = []
          text.push(`<ul class="${chart.id}-legend">`)
          for (let i = 0; i < chart.data.datasets.length; i++) {
            const label = chart.data.datasets[i].label
            let sum = 0
            if (label === 'Click Rate') {
              // calculate the average Click Rate
              const deliveredTotal = (
                chart.data.datasets.find(item => item.label === 'Delivered') ||
                // Use Sent for Mobile push
                chart.data.datasets.find(item => item.label === 'Sent')
              ).data.reduce((s, a) => s + a, 0)
              const clicked = chart.data.datasets
                .find(item => item.label === 'Clicked')
                .data.reduce((partialSum, a) => partialSum + a, 0)

              sum =
                clicked && deliveredTotal
                  ? ((clicked / deliveredTotal) * 100).toFixed(2) + '%'
                  : '0%'
            } else if (label === 'Open Rate') {
              // calculate the average Open Rate
              const deliveredTotal = (
                chart.data.datasets.find(item => item.label === 'Delivered') ||
                // Use Sent for Mobile push
                chart.data.datasets.find(item => item.label === 'Sent')
              ).data.reduce((s, a) => s + a, 0)
              const opened = chart.data.datasets
                .find(item => item.label === 'Opened')
                .data.reduce((partialSum, a) => partialSum + a, 0)

              sum =
                opened && deliveredTotal
                  ? ((opened / deliveredTotal) * 100).toFixed(2) + '%'
                  : '0%'
            } else if (
              chart.data.datasets[i].hasOwnProperty('total') &&
              chart.data.datasets[i].total !== null
            ) {
              // Total for variant testing chart. click rate and open rate
              sum = chart.data.datasets[i].total + '%'
            } else {
              sum = chart.data.datasets[i].data.reduce(
                (partialSum, a) => partialSum + a,
                0
              )
            }

            text.push(
              '<li class="legend-item">' +
                `<div class="point" style="background-color:${chart.data.datasets[i].borderColor}">&nbsp;</div>` +
                `<div class="label-ct">
                    <span class="label">
                        ${
                          label.length > 23
                            ? `${label.substring(0, 20)}...`
                            : label
                        }
                    </span></br>
                    <span class="sum">${sum}</span></div>` +
                '</li>'
            )
          }
          text.push('</ul>')
          return text.join('')
        }
      }
    }
  },
  computed: {
    methods() {
      const methods = _.cloneDeep(this.statMethods)

      if (this.$store.state.app.features.showInboxOpenStats) {
        methods.inbox.push('inbox_opened', 'inbox_open_rate')
      }

      return methods
    },
    combinedChartOptions() {
      return { ...this.defaultChartOptions, ...this.chartOptions }
    }
  },
  methods: {
    getMaxValue() {
      if (this.chartData.datasets) {
        const datasets = this.chartData.datasets.filter(
          item => !item.label.includes('Rate')
        )
        const max =
          Math.max(...datasets.map(item => Math.max(...item.data))) || 1

        return Math.ceil(max / 5) * 5
      }

      return 1
    },
    getTicksNumber() {
      // calculate number of ticks and gridlines to show.
      const maxValue = this.getMaxValue()
      const maxTicksLimit = 5
      for (let number = maxTicksLimit; number >= 0; number--) {
        if (!this.alignTicksWithSecondAxis && maxValue % number === 0) {
          return number
        }
        if (
          this.alignTicksWithSecondAxis &&
          maxValue % number === 0 &&
          100 % number === 0
        ) {
          return number
        }
      }

      return 1
    },
    calculateStepSizeY0() {
      return this.getMaxValue() / this.getTicksNumber()
    },
    calculateStepSizeY1() {
      return 100 / this.getTicksNumber()
    },
    bindChartEvents(chart) {
      const legendItems = [
        ...document
          .getElementById(this.legendId)
          .querySelectorAll('.legend-item')
      ]

      legendItems.forEach((item, i) => {
        item.addEventListener('click', e => updateDataset(item, i))
      })

      const updateDataset = (currentEl, index) => {
        const meta = chart._data._chart._getSortedDatasetMetas()[index]
        const labelEl = currentEl.querySelector('.label')
        labelEl.style.textDecoration = meta.hidden ? 'none' : 'line-through'
        meta.hidden = !meta.hidden

        chart._data._chart.update()
      }
    },

    generateLegend() {
      const chart = this.$refs['chart-' + this.legendId]
      document.getElementById(this.legendId).innerHTML = chart.generateLegend()
      this.bindChartEvents(chart, document)
    }
  },

  mounted() {
    this.generateLegend()
  }
}
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 24px;
}

h3 {
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #1d87c8;
  display: inline-block;
}

.icon {
  color: #1d87c8;
  width: 32px;
  height: 32px;
  margin-right: 12px;
  margin-left: 2px;
}

.chart-ct {
  display: flex;
}

.legend-ct {
  width: 15%;
  margin-left: 20px;
  margin-top: 4px;
}

>>> .legend-item {
  display: flex;
  margin-bottom: 20px;
}

>>> .legend-item .point {
  width: 11px;
  height: 11px;
  margin-top: 3px;
  margin-right: 5px;
  cursor: pointer;
}

>>> .legend-item .label {
  font-family: Open Sans, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  text-align: left;
  cursor: pointer;
}

>>> .legend-item .sum {
  font-family: Open Sans, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/common/components/ChannelMetricsChart.vue